import { ActionButton } from '@react-spectrum/s2';
import Close from '@react-spectrum/s2/icons/Close';
import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

const GAP_PX = 8;
const FALLBACK_HEIGHT_PX = 200;
const FALLBACK_WIDTH_PX = 320;
const MIN_PANEL_HEIGHT_PX = 120;
const VIEWPORT_HEIGHT_PERCENT = 70;

type PopupPortalProps = {
	anchorRect: DOMRect;
	children: ReactNode;
	onDismiss: () => void;
	isAnchorVisible: boolean;
	frameRect: DOMRect | null;
};

export const PopupPortal = ({
	anchorRect,
	children,
	onDismiss,
	isAnchorVisible,
	frameRect,
}: PopupPortalProps) => {
	const containerRef = useRef<HTMLDivElement>(null);

	// Clicks inside the email are handled by useIframeInteraction, which owns the
	// selection. This listener only covers the surrounding page.
	useEffect(() => {
		const handleParentClick = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				onDismiss();
			}
		};

		// Delay listener attachment to avoid catching the triggering click
		const timer = setTimeout(() => {
			document.addEventListener('mousedown', handleParentClick);
		}, 0);

		return () => {
			clearTimeout(timer);
			document.removeEventListener('mousedown', handleParentClick);
		};
	}, [onDismiss]);

	// Measure the real panel box so the flip and clamp below use its true size.
	const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
	const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);

	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const measure = () => {
			setMeasuredHeight(container.offsetHeight);
			setMeasuredWidth(container.offsetWidth);
		};
		measure();

		// A popup body can grow on its own state, such as the colour picker
		// expanding. That changes no prop here, so watch the box itself. Keying
		// this off props left the old measurement in place and the panel then
		// hung below the bottom of the window.
		if (typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(measure);
		observer.observe(container);
		return () => observer.disconnect();
	}, []);

	const viewportHeight = window.innerHeight;
	const viewportWidth = window.innerWidth;
	// A measurement of 0 means the panel has not been laid out yet, so fall back
	// to a nominal size rather than letting the flip and clamp math collapse.
	const popupWidth = measuredWidth || FALLBACK_WIDTH_PX;

	// Bound the panel by the email frame, not by the window. The window is far
	// larger than the frame, so a window-only clamp let the panel draw outside
	// the email and over the app around it. A press on that overhang neither
	// reaches the covered control nor closes the panel.
	const windowTop = GAP_PX;
	const windowBottom = viewportHeight - GAP_PX;
	const windowLeft = GAP_PX;
	const windowRight = viewportWidth - GAP_PX;

	const frameTop = frameRect ? Math.max(frameRect.top, windowTop) : windowTop;
	const frameBottom = frameRect
		? Math.min(frameRect.bottom, windowBottom)
		: windowBottom;
	const frameLeft = frameRect
		? Math.max(frameRect.left, windowLeft)
		: windowLeft;
	const frameRight = frameRect
		? Math.min(frameRect.right, windowRight)
		: windowRight;

	// Cap the panel to the email band instead of letting a tall panel escape it.
	// A panel taller than the frame has no placement inside it, and the window
	// fallback then drew it over the app navbar and the subject row. The body
	// already scrolls, so a shorter panel loses no control. Below
	// MIN_PANEL_HEIGHT_PX the panel stops being usable, so a frame that thin
	// still takes the window band.
	// Integer arithmetic, because a fractional cap reaches the DOM as a style.
	const viewportCap = Math.floor(
		(viewportHeight * VIEWPORT_HEIGHT_PERCENT) / 100,
	);
	const maxPanelHeight = Math.max(
		MIN_PANEL_HEIGHT_PX,
		Math.floor(Math.min(viewportCap, frameBottom - frameTop)),
	);
	// The cap reaches the DOM as a style, so a stale measurement can outlive it
	// by one frame. Clamp here too, otherwise that frame takes the fallback and
	// flashes the panel over the app.
	const popupHeight = Math.min(
		measuredHeight || FALLBACK_HEIGHT_PX,
		maxPanelHeight,
	);

	const fitsInFrameHeight = frameBottom - frameTop >= popupHeight;
	const fitsInFrameWidth = frameRight - frameLeft >= popupWidth;
	const minTop = fitsInFrameHeight ? frameTop : windowTop;
	const maxTop = (fitsInFrameHeight ? frameBottom : windowBottom) - popupHeight;
	const minLeft = fitsInFrameWidth ? frameLeft : windowLeft;
	const maxLeft = (fitsInFrameWidth ? frameRight : windowRight) - popupWidth;

	const belowTop = anchorRect.bottom + GAP_PX;
	const fitsBelow = belowTop <= maxTop;
	const preferredTop = fitsBelow
		? belowTop
		: anchorRect.top - popupHeight - GAP_PX;

	const top = Math.max(minTop, Math.min(preferredTop, maxTop));
	const left = Math.max(minLeft, Math.min(anchorRect.left, maxLeft));

	return createPortal(
		<div
			ref={containerRef}
			data-bw-editor-popup="1"
			className="fixed z-50 flex max-w-[320px] min-w-[220px] flex-col rounded-xl
				border border-gray-200 bg-white p-3 shadow-xl"
			style={{ top, left, maxHeight: maxPanelHeight }}
		>
			<div className="flex shrink-0 justify-end">
				<ActionButton isQuiet aria-label="Close edit panel" onPress={onDismiss}>
					<Close />
				</ActionButton>
			</div>
			<div
				data-bw-editor-popup-body="1"
				className="min-h-0 flex-1 overflow-y-auto"
				style={
					// The anchored element has scrolled out of the email frame. Hiding
					// the body keeps its controls out of reach, so no edit lands on an
					// element nobody can see. The selection survives, so scrolling back
					// brings it straight back. The close button above stays reachable
					// regardless, since it's not gated on the anchor's visibility.
					// `visibility` rather than `display` keeps the box measurable.
					isAnchorVisible
						? undefined
						: {
								visibility: 'hidden' as const,
								pointerEvents: 'none' as const,
							}
				}
			>
				{children}
			</div>
		</div>,
		document.body,
	);
};
